import { gql } from '@apollo/client';
import { Box, Center, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { t } from 'i18next';
import { ReactNode, useMemo } from 'react';
import { Column, TableInstance, useRowSelect, useSortBy, useTable } from 'react-table';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { useAnalyticsReporter } from '~utils/analytics';
import { compareMaybeStrings } from '~utils/compare';
import { MaybePromise } from '~utils/types';
import { ConfigurationTable_ConfigurationFragment } from './__generated__/ConfigurationsTable.graphql';
import { CreatedOnCell } from './Cell/CreatedOnCell';
import { DescriptionCell } from './Cell/DescriptionCell';
import { DraftStatusCell } from './Cell/DraftStatusCell';
import { DuplicateCell } from './Cell/DuplicateCell';
import { EditDeleteCell } from './Cell/EditDeleteCell';
import { NameCell } from './Cell/NameCell';
import { PlatformCell } from './Cell/PlatformCell';
import { StatusCell } from './Cell/StatusCell';
import { SearchBar } from './SearchBar';

export enum Columns {
  Selection = 'selection',
  Status = 'status',
  Name = 'configurationName',
  Description = 'description',
  Model = 'model',
  CreatedOn = 'createdOn',
  DraftStatus = 'draftStatus',
  Duplicate = 'duplicate',
  EditDelete = 'editDelete',
}

export function useConfigurationTable(data: ConfigurationTable_ConfigurationFragment[]) {
  const columns = useMemo<Array<Column<ConfigurationTable_ConfigurationFragment>>>(
    () => [
      {
        id: Columns.Status,
        Header: t('status'),
        accessor: (p) => p.state,
        Cell: StatusCell,
        minWidth: 130,
        maxWidth: 150,
      },
      {
        id: Columns.Name,
        Header: t('name'),
        accessor: (p) => p.name,
        Cell: NameCell,
        minWidth: 170,
        maxWidth: 180,
      },
      {
        id: Columns.Description,
        Header: t('description'),
        accessor: (p) => p.description,
        sortType: (rowA, rowB) =>
          compareMaybeStrings(rowA.original.description, rowB.original.description),
        Cell: DescriptionCell,
        minWidth: 200,
        maxWidth: 250,
      },
      {
        id: Columns.Model,
        Header: t('model'),
        accessor: (p) => p.platform,
        Cell: PlatformCell,
        minWidth: 110,
        maxWidth: 100,
      },
      {
        id: Columns.CreatedOn,
        Header: t('createdOn'),
        accessor: (d) => d.createdAt,
        Cell: CreatedOnCell,
        width: 'auto',
      },
      {
        id: Columns.DraftStatus,
        Header: t('draftStatus'),
        accessor: (d) => (d.draft?.id ? d.draft?.state : null),
        Cell: DraftStatusCell,
        width: 'auto',
      },
      {
        id: Columns.Duplicate,
        Header: () => <Center>{t('duplicate').toUpperCase()}</Center>,
        accessor: (d) => d,
        Cell: DuplicateCell,
        width: 'auto',
      },
      {
        id: Columns.EditDelete,
        Header: () => <Center>{`${t('edit')}/${t('delete')}`}</Center>,
        accessor: (d) => d,
        Cell: EditDeleteCell,
        width: 'auto',
      },
    ],
    [],
  );

  return useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
    },
    useSortBy,
    useRowSelect,
  );
}

/* eslint-disable react/jsx-key */
export function ConfigurationTable({
  table,
  onGoToDetail,
  hasSearchResults,
}: {
  table: TableInstance<ConfigurationTable_ConfigurationFragment>;
  onGoToDetail: (config: ConfigurationTable_ConfigurationFragment) => MaybePromise<void>;
  hasSearchResults: boolean;
}) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = table;
  const analytics = useAnalyticsReporter();

  return (
    <Table {...getTableProps()} variant="simple" width="100%">
      <Thead>
        {headerGroups.map((headerGroup) => (
          <Tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              const headerProps = column.getHeaderProps(column.getSortByToggleProps());
              return (
                <Th
                  {...headerProps}
                  color={column.isSorted ? 'gray.700' : 'gray.600'}
                  minWidth={`${column.minWidth}px`}
                  maxWidth={`${column.maxWidth}px`}
                  width={column.width}
                  _hover={
                    column.disableSortBy
                      ? {}
                      : {
                          textColor: 'gray.800',
                        }
                  }
                  onClick={(e) => {
                    if (column.id !== 'selection') {
                      analytics.track('scheduleTableSort', { sortType: column.id });
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (headerProps as any).onClick?.(e);
                  }}
                >
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <Box flex="1">{column.render('Header') as ReactNode}</Box>
                    {column.canSort && (
                      <Box>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <ChevronDownIcon display="inherit" />
                          ) : (
                            <ChevronUpIcon display="inherit" />
                          )
                        ) : (
                          <ChevronDownIcon visibility="hidden" />
                        )}
                      </Box>
                    )}
                  </Box>
                </Th>
              );
            })}
          </Tr>
        ))}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {hasSearchResults ? (
          rows.map((row) => {
            prepareRow(row);

            return (
              <Tr
                {...row.getRowProps()}
                role="button"
                _hover={{
                  background: 'blue.50',
                  cursor: 'pointer',
                }}
              >
                {row.cells.map((cell) => {
                  return (
                    <Td
                      minWidth={`${cell.column.minWidth}px`}
                      maxWidth={`${cell.column.maxWidth}px`}
                      width={cell.column.width}
                      onClick={() => {
                        analytics.track('scheduleTableItemClick');
                        onGoToDetail(row.original);
                      }}
                      {...cell.getCellProps()}
                    >
                      {cell.render('Cell') as ReactNode}
                    </Td>
                  );
                })}
              </Tr>
            );
          })
        ) : (
          <Tr>
            <Td colSpan={table?.columns?.length || 1} color="gray.500" fontWeight="medium">
              {t('noResultsFound')}
            </Td>
          </Tr>
        )}
      </Tbody>
    </Table>
  );
}

ConfigurationTable.SearchSelector = SearchBar;

ConfigurationTable.graphql = {
  fragments: {
    ConfigurationTable_configuration: gql`
      fragment ConfigurationTable_configuration on ConfigurationModel {
        id
        customerId
        platform
        draft {
          id
          name
          description
          state
          platform
        }
        ...NameCell_configuration
        ...StatusCell_configuration
        ...DescriptionCell_configuration
        ...PlatformCell_configuration
        ...CreatedOnCell_configuration
        ...DraftStatusCell_configuration
      }
    `,
  },
};
